import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import {
  useContact,
  useContactHistory,
  useContacts,
  useMergeContacts,
  useUpdateContact,
  type ChannelRow,
  type ContactRow,
  type OperatorRow,
  type TicketRow,
} from '../api/queries.js';
import {
  Button,
  ChannelIcon,
  ConfirmModal,
  DrawerPanel,
  Field,
  InlineTagEditor,
  Input,
  OperatorAvatar,
  SLATimer,
  StatusChip,
  timeAgo,
  type ChannelType,
  type TicketStatus,
} from '../components/ui/index.js';

import { CommercePanel } from './commerce-panel.js';
import { insertIntoComposer } from './composer-bus.js';

interface Props {
  workspaceSlug: string | undefined;
  ticket: TicketRow;
  contactId: string;
  channel: ChannelRow | undefined;
  assignedOperator: OperatorRow | undefined;
}

interface ExtendedContact {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  tags: string[];
  attributes?: { city?: string; segment?: string; total_orders?: number; total_amount?: number };
  channelIds?: Record<string, string>;
  geo?: { city?: string; country?: string };
  createdAt?: string;
  lastSeenAt: string | null;
}

export function ContactPanel(props: Props): JSX.Element {
  const navigate = useNavigate();
  const contactQ = useContact(props.workspaceSlug, props.contactId);
  const historyQ = useContactHistory(props.workspaceSlug, props.contactId);
  const updateContact = useUpdateContact(props.workspaceSlug);
  const mergeContacts = useMergeContacts(props.workspaceSlug);
  const c = contactQ.data as ExtendedContact | undefined;

  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergePick, setMergePick] = useState<ContactRow | null>(null);
  // Список кандидатов на объединение — грузим только когда drawer открыт.
  const mergeList = useContacts(mergeOpen ? props.workspaceSlug : undefined, mergeSearch);

  if (!c) {
    return (
      <div className="inbx-contact" style={{ padding: 16 }}>
        Загрузка…
      </div>
    );
  }

  const attrs = c.attributes ?? {};
  const channels = Object.keys(c.channelIds ?? {}) as ChannelType[];

  function saveTags(tags: string[]): void {
    updateContact.mutate({ id: c!.id, patch: { tags } });
  }

  return (
    <div className="inbx-contact">
      <div className="inbx-contact__header">
        <OperatorAvatar user={{ id: c.id, name: c.name ?? 'Клиент' }} size="lg" />
        <div className="inbx-contact__name">{c.name ?? 'Анонимный клиент'}</div>
        <div className="inbx-contact__sub">
          Клиент с{' '}
          {c.createdAt
            ? new Date(c.createdAt).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
            : '—'}
        </div>
        {channels.length > 0 && (
          <div className="inbx-contact__channels">
            {channels.map((ch) => (
              <ChannelIcon key={ch} channel={ch as ChannelType} size={18} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, width: '100%' }}>
          <Button
            icon="ti-user-circle"
            variant="secondary"
            onClick={() => void navigate({ to: '/contacts', search: { id: c.id } })}
            style={{ flex: 1 }}
          >
            Профиль
          </Button>
          <Button
            icon="ti-arrows-join"
            variant="secondary"
            onClick={() => setMergeOpen(true)}
            style={{ flex: 1 }}
          >
            Объединить
          </Button>
        </div>
      </div>

      {/* Контакт */}
      <div className="inbx-contact__section">
        <div className="inbx-contact__section-h">
          Контакт{' '}
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            style={{ color: 'var(--inbx-accent)', fontSize: 'var(--inbx-fz-sm)' }}
          >
            Изменить
          </button>
        </div>
        {c.email && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Email</span>
            <span className="inbx-contact__row-val">{c.email}</span>
          </div>
        )}
        {c.phone && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Телефон</span>
            <span className="inbx-contact__row-val">{c.phone}</span>
          </div>
        )}
        {attrs.city && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Город</span>
            <span className="inbx-contact__row-val">{attrs.city}</span>
          </div>
        )}
        {attrs.total_orders != null && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Заказов</span>
            <span className="inbx-contact__row-val">
              {attrs.total_orders} на{' '}
              {Math.round((attrs.total_amount ?? 0) / 100).toLocaleString('ru-RU')} ₽
            </span>
          </div>
        )}
        {attrs.segment && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Сегмент</span>
            <span className="inbx-contact__row-val">
              <SegmentBadge value={attrs.segment} />
            </span>
          </div>
        )}
      </div>

      {/* Покупки и товары из внешних систем. Панель сама решает, показываться
          ли ей: без подключённых источников её просто нет. */}
      <CommercePanel
        workspaceSlug={props.workspaceSlug}
        ticketId={props.ticket.id}
        onInsertText={insertIntoComposer}
      />

      {/* Теги — B6 */}
      <div className="inbx-contact__section">
        <div className="inbx-contact__section-h">Теги</div>
        <InlineTagEditor value={c.tags ?? []} onChange={saveTags} placeholder="+ добавить тег" />
      </div>

      {/* Текущий тикет */}
      <div className="inbx-contact__section">
        <div className="inbx-contact__section-h">Текущий тикет</div>
        {props.assignedOperator && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Оператор</span>
            <span className="inbx-contact__row-val">{props.assignedOperator.name}</span>
          </div>
        )}
        {props.channel && (
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Канал</span>
            <span className="inbx-contact__row-val">{props.channel.name}</span>
          </div>
        )}
        <div className="inbx-contact__row">
          <span className="inbx-contact__row-label">Открыт</span>
          <span className="inbx-contact__row-val">{timeAgo(props.ticket.createdAt)}</span>
        </div>
        <div className="inbx-contact__row">
          <span className="inbx-contact__row-label">SLA</span>
          <span className="inbx-contact__row-val">
            <SLATimer deadline={props.ticket.slaDeadline} breached={props.ticket.slaBreached} />
          </span>
        </div>
      </div>

      {/* История */}
      <div className="inbx-contact__section">
        <div className="inbx-contact__section-h">История обращений</div>
        <div className="inbx-contact__history">
          {(historyQ.data ?? []).slice(0, 8).map((t) => (
            <div key={t.id} className="inbx-contact__hist-row">
              <StatusChip status={t.status as TicketStatus} size="sm" />
              <span className="inbx-contact__hist-title">{t.subject ?? `#${t.number}`}</span>
              <span className="inbx-contact__hist-time">{timeAgo(t.createdAt)}</span>
            </div>
          ))}
          {(historyQ.data ?? []).length === 0 && (
            <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              Нет других обращений
            </span>
          )}
        </div>
      </div>

      {/* B5: EditContactDrawer */}
      <EditContactDrawer
        open={editOpen}
        contact={c}
        onClose={() => setEditOpen(false)}
        onSave={(patch) => {
          updateContact.mutate({ id: c.id, patch });
          setEditOpen(false);
        }}
      />

      {/* Объединение: выбираем дубликат, который вольётся в текущий контакт */}
      <DrawerPanel
        open={mergeOpen}
        title="Объединить контакты"
        onClose={() => {
          setMergeOpen(false);
          setMergePick(null);
        }}
        width={380}
      >
        <p style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
          Выберите дубликат — его тикеты, сообщения и каналы перейдут в текущий контакт
          <strong> {c.name ?? c.email ?? c.phone ?? 'без имени'}</strong>, а сам дубликат будет
          помечен объединённым.
        </p>
        <Field label="Поиск контакта">
          <Input
            value={mergeSearch}
            onChange={(e) => setMergeSearch(e.target.value)}
            placeholder="имя, телефон или email"
          />
        </Field>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          {(mergeList.data ?? [])
            .filter((x) => x.id !== c.id)
            .slice(0, 12)
            .map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setMergePick(x)}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: 'var(--inbx-radius)',
                  border: '1px solid var(--inbx-border)',
                  background: 'var(--inbx-bg)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 500 }}>{x.name ?? 'Без имени'}</div>
                <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
                  {x.email ?? x.phone ?? '—'}
                </div>
              </button>
            ))}
          {(mergeList.data ?? []).filter((x) => x.id !== c.id).length === 0 && (
            <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              Ничего не найдено
            </span>
          )}
        </div>
      </DrawerPanel>

      <ConfirmModal
        open={!!mergePick}
        title="Объединить контакты?"
        description={`«${mergePick?.name ?? mergePick?.email ?? mergePick?.phone ?? ''}» будет влит в текущий контакт. Тикеты и каналы перейдут сюда, дубликат пометится объединённым. Действие необратимо.`}
        confirmLabel="Объединить"
        variant="danger"
        onConfirm={() => {
          if (mergePick) {
            mergeContacts.mutate({ primary_id: c.id, duplicate_ids: [mergePick.id] });
          }
          setMergePick(null);
          setMergeOpen(false);
          setMergeSearch('');
        }}
        onCancel={() => setMergePick(null)}
      />
    </div>
  );
}

// ── B5: Edit Contact Drawer ──────────────────────────────────────────

function EditContactDrawer({
  open,
  contact,
  onClose,
  onSave,
}: {
  open: boolean;
  contact: ExtendedContact;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => void;
}): JSX.Element {
  const [name, setName] = useState(contact.name ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [phone, setPhone] = useState(contact.phone ?? '');

  // Переинициализируем поля при открытии и при смене контакта — иначе значения
  // от предыдущего контакта «прилипают» (компонент смонтирован постоянно).
  // Зависим только от open+id: фоновый refetch (WS) не должен сбрасывать ввод.
  useEffect(() => {
    if (open) {
      setName(contact.name ?? '');
      setEmail(contact.email ?? '');
      setPhone(contact.phone ?? '');
    }
    // Зависимости намеренно неполные: поля формы перезаполняются при смене
    // контакта или переоткрытии, а не на каждое обновление объекта — иначе
    // правки оператора затирались бы фоновым перезапросом.
  }, [open, contact.id]);

  if (!open) return <></>;

  function handleSave(): void {
    const patch: Record<string, unknown> = {};
    // Сравниваем по «нормализованному» значению: пустое поле ≡ отсутствие значения
    // (contact.* может быть null). Иначе пустой email улетал как null и валил весь патч.
    // Шлём только реально изменённые поля; очистку поля отправляем как null.
    const nextName = name.trim();
    const nextEmail = email.trim();
    const nextPhone = phone.trim();
    if (nextName !== (contact.name ?? '')) patch.name = nextName || null;
    if (nextEmail !== (contact.email ?? '')) patch.email = nextEmail || null;
    if (nextPhone !== (contact.phone ?? '')) patch.phone = nextPhone || null;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    onSave(patch);
  }

  return (
    <DrawerPanel open={open} title="Редактировать контакт" onClose={onClose} width={360}>
      <Field label="Имя">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Имя клиента" />
      </Field>
      <Field label="Email">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
        />
      </Field>
      <Field label="Телефон">
        <Input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+7 999 123-45-67"
        />
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose}>
          Отмена
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Сохранить
        </Button>
      </div>
    </DrawerPanel>
  );
}

function SegmentBadge({ value }: { value: string }): JSX.Element {
  const colors: Record<string, { bg: string; text: string }> = {
    vip: { bg: '#FAEEDA', text: '#633806' },
    gold: { bg: '#EEEDFE', text: '#3C3489' },
    regular: { bg: '#F1EFE8', text: '#5F5E5A' },
  };
  const c = colors[value] ?? colors.regular!;
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        padding: '1px 8px',
        borderRadius: 10,
        fontWeight: 500,
        fontSize: 11,
      }}
    >
      {value.toUpperCase()}
    </span>
  );
}
